import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { ROLE_LABELS } from "@/lib/roles";
import { initials } from "@/lib/format";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { UserPlus, Trash2, ShieldCheck, Stethoscope, Headset } from "lucide-react";

const ROLE_META = {
  admin: { icon: ShieldCheck, c: "bg-slate-900 text-white", badge: "bg-slate-100 text-slate-700 border-slate-200" },
  provider: { icon: Stethoscope, c: "bg-cyan-600 text-white", badge: "bg-cyan-50 text-cyan-700 border-cyan-200" },
  front_desk: { icon: Headset, c: "bg-emerald-600 text-white", badge: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};
const ACCESS_SUMMARY = {
  admin: "Full access to every module & settings",
  provider: "Calendar, patient communication & medications",
  front_desk: "Lead pipeline, calendar, communication & medications",
};

export default function Staff() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "front_desk" });

  const { data: staff = [] } = useQuery({ queryKey: ["staff"], queryFn: async () => (await api.get("/staff")).data });

  const create = useMutation({
    mutationFn: async () => (await api.post("/staff", form)).data,
    onSuccess: () => {
      toast.success("Team member added");
      qc.invalidateQueries({ queryKey: ["staff"] });
      setOpen(false);
      setForm({ name: "", email: "", password: "", role: "front_desk" });
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not add member"),
  });

  const changeRole = useMutation({
    mutationFn: async ({ id, role }) => (await api.put(`/staff/${id}/role`, { role })).data,
    onSuccess: () => { toast.success("Role updated"); qc.invalidateQueries({ queryKey: ["staff"] }); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not update role"),
  });

  const remove = useMutation({
    mutationFn: async (id) => (await api.delete(`/staff/${id}`)).data,
    onSuccess: () => { toast.success("Team member removed"); qc.invalidateQueries({ queryKey: ["staff"] }); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not remove member"),
  });

  return (
    <Layout title="Staff & Roles" subtitle="Control who can see and do what across your practice"
      actions={
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-staff-button" className="bg-slate-900 hover:bg-slate-800 text-white">
              <UserPlus className="h-4 w-4 mr-2" /> Add Team Member
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader><DialogTitle className="font-heading">Add Team Member</DialogTitle>
              <DialogDescription>Create a login and assign an access level for this person.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div className="space-y-1.5"><Label>Full name</Label>
                <Input data-testid="staff-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Jordan Avery" /></div>
              <div className="space-y-1.5"><Label>Email</Label>
                <Input data-testid="staff-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="jordan@themensclinic.com" /></div>
              <div className="space-y-1.5"><Label>Temporary password</Label>
                <Input data-testid="staff-password-input" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="At least 8 characters" />
                <p className="text-[11px] text-slate-400">They'll be asked to replace it with their own password at first sign-in.</p></div>
              <div className="space-y-1.5"><Label>Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="staff-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Owner / Admin</SelectItem>
                    <SelectItem value="provider">Provider</SelectItem>
                    <SelectItem value="front_desk">Front Desk</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-400">{ACCESS_SUMMARY[form.role]}</p>
              </div>
            </div>
            <DialogFooter>
              <Button data-testid="staff-save-button" className="w-full bg-slate-900 hover:bg-slate-800 text-white"
                disabled={!form.name || !form.email || form.password.length < 8 || create.isPending}
                onClick={() => create.mutate()}>
                {create.isPending ? "Adding…" : "Add Member"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      }
    >
      {/* Role legend */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        {["admin", "provider", "front_desk"].map((r) => {
          const m = ROLE_META[r];
          return (
            <div key={r} className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
              <div className="flex items-center gap-2 mb-1.5">
                <span className={`h-7 w-7 rounded-lg flex items-center justify-center ${m.c}`}><m.icon className="h-4 w-4" /></span>
                <span className="font-semibold text-slate-900">{ROLE_LABELS[r]}</span>
              </div>
              <p className="text-xs text-slate-500">{ACCESS_SUMMARY[r]}</p>
            </div>
          );
        })}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100">
          <h3 className="font-heading font-bold text-slate-900">Team Members ({staff.length})</h3>
        </div>
        <div className="divide-y divide-slate-50">
          {staff.map((u, i) => {
            const m = ROLE_META[u.role] || ROLE_META.front_desk;
            return (
              <motion.div key={u.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
                data-testid={`staff-row-${u.id}`}
                className="px-6 py-4 flex flex-wrap items-center gap-4 hover:bg-slate-50 transition-colors">
                <div className={`h-10 w-10 rounded-full flex items-center justify-center text-sm font-bold shrink-0 ${m.c}`}>
                  {initials(u.name)}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-slate-900 flex items-center gap-2">
                    {u.name}
                    {u.is_self && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">YOU</span>}
                  </div>
                  <div className="text-xs text-slate-400 truncate">{u.email}</div>
                </div>
                <div className="flex items-center gap-2">
                  {u.is_self ? (
                    <span className={`text-xs font-semibold px-2.5 py-1.5 rounded-lg border ${m.badge}`}>{ROLE_LABELS[u.role]}</span>
                  ) : (
                    <Select value={u.role} onValueChange={(v) => changeRole.mutate({ id: u.id, role: v })}>
                      <SelectTrigger data-testid={`staff-role-${u.id}`} className="w-36 h-9"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="admin">Owner / Admin</SelectItem>
                        <SelectItem value="provider">Provider</SelectItem>
                        <SelectItem value="front_desk">Front Desk</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                  <Button data-testid={`staff-delete-${u.id}`} variant="ghost" size="icon"
                    disabled={u.is_self || remove.isPending}
                    onClick={() => remove.mutate(u.id)}
                    className="text-slate-400 hover:text-red-600 disabled:opacity-30">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </Layout>
  );
}
